import React, { Fragment, useCallback, useEffect } from 'react';

import { ICartEntry } from '@rbi-ctg/menu';
import useCartAddOns from 'hooks/use-cart-add-ons';
import useFeatureMenuAddOn, { IAddOnSectionOption } from 'hooks/use-feature-menu-add-on';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames } from 'state/mParticle';
import { useOrderContext } from 'state/order';

import CartItem from '../cart-item/cart-item';

import { AddOnFreeModal } from './components/addons-free-modal';
import { useAddOnsItemContainer } from './hook/use-addons-item-container';
import { SectionHeading, SectionItemContainer, SectionItems } from './styled';

export const AddOnsItemsContainer = () => {
  const { sections: addOnSections } = useFeatureMenuAddOn();
  const { cartEntries } = useOrderContext();
  const cartEntry: ICartEntry[] = cartEntries;
  const {
    existsAllAddonsInCart,
    isServiceValid,
    removeList,
    showPaidItemForFreeInSection,
    isItemsAcceptingFreeOffer,
    addOnHasOffer,
    checkAndAdjustItems,
  } = useAddOnsItemContainer();
  const { handleAddItemToCart } = useCartAddOns();
  const enableFreeAddonOnCartFeatureFlag = useFlag(LaunchDarklyFlag.ENABLE_FREE_ADDON_ON_CART);

  useEffect(() => {
    removeList(cartEntry, addOnSections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOnSections]);

  useEffect(() => {
    if (enableFreeAddonOnCartFeatureFlag) {
      checkAndAdjustItems(cartEntry, addOnSections);
    }
  }, [addOnSections, cartEntry, checkAndAdjustItems, enableFreeAddonOnCartFeatureFlag]);

  const addOnSectionsShow = addOnSections.filter(
    item => item.showSectionItemsOnCart && isServiceValid(item.sectionItemsServiceModes)
  );

  const renderCartItems = useCallback(
    (
      { option, freeOption, itemsAcceptingFreeOffers }: IAddOnSectionOption,
      enableAddonAsFreeItem: boolean
    ) => {
      const isFreeAddonEnabled = enableFreeAddonOnCartFeatureFlag && enableAddonAsFreeItem;

      const showPaidItem = showPaidItemForFreeInSection(
        cartEntries,
        freeOption,
        itemsAcceptingFreeOffers
      );

      const isInCart = cartEntry.some(entry => {
        if (!isFreeAddonEnabled) {
          return entry._id === option._id;
        }

        return showPaidItem ? entry._id === option._id : entry._id === freeOption?._id;
      });

      const hasOffer = addOnHasOffer(cartEntries, itemsAcceptingFreeOffers);

      const item = !showPaidItem && isFreeAddonEnabled && freeOption ? freeOption : option;

      return isInCart || (isFreeAddonEnabled && !hasOffer) ? null : (
        <CartItem
          item={item}
          key={option._id}
          addToCard={handleAddItemToCart}
          logEventData={{
            logEventName: CustomEventNames.ADDONS_ITEM_DISPLAYED,
          }}
        />
      );
    },
    [
      addOnHasOffer,
      cartEntries,
      cartEntry,
      enableFreeAddonOnCartFeatureFlag,
      handleAddItemToCart,
      showPaidItemForFreeInSection,
    ]
  );

  return (
    <>
      {addOnSectionsShow.map(
        section =>
          !existsAllAddonsInCart(section, cartEntry) &&
          isItemsAcceptingFreeOffer(section, cartEntry) && (
            <Fragment key={section._id}>
              <SectionItemContainer>
                <SectionHeading>{section.name.locale}</SectionHeading>

                <SectionItems>
                  {section.options.map(option =>
                    renderCartItems(option, section.enableAddonAsFreeItem)
                  )}
                </SectionItems>
              </SectionItemContainer>
            </Fragment>
          )
      )}
      <AddOnFreeModal
        cartEntries={cartEntries}
        addOnSections={addOnSections}
        addItemToCart={handleAddItemToCart}
      />
    </>
  );
};

export default AddOnsItemsContainer;
